import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  getBulkUploadStatusAPI,
  getBulkUploadFailedItemsAPI,
  getBulkUploadJobsAPI,
} from "../../../../../api/index";
import DeadlineCountdown from "./DeadlineCountdown";
import { getKoreaNowDate } from "../../../../../utils/serverTime";
import "./BulkExcelModal.css";

const STEPS = [
  { id: "ready", label: "서식·신청" },
  { id: "validate", label: "검증" },
  { id: "result", label: "결과·신청" },
];

const POLL_INTERVAL_MS = 3000;

/** KST 기준 다음 end_submit(예: 17:00)까지 남은 ms */
const getMsUntilNextEndSubmitKst = (endSubmitTime = "17:00") => {
  const [endHour, endMinute] = String(endSubmitTime)
    .split(":")
    .map((v) => parseInt(v, 10));
  if (!Number.isFinite(endHour) || !Number.isFinite(endMinute)) return null;

  const koreaNow = getKoreaNowDate();
  const target = new Date(koreaNow.getTime());
  target.setUTCHours(endHour, endMinute || 0, 0, 0);

  if (koreaNow.getTime() >= target.getTime()) {
    target.setUTCDate(target.getUTCDate() + 1);
  }

  return Math.max(0, target.getTime() - koreaNow.getTime());
};

const formatJobDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const failRowLabel = (rawRow = {}) =>
  String(
    rawRow["메인키워드"] ||
      rawRow["카테고리 상품명(원부)"] ||
      rawRow["상품 링크"] ||
      "행 정보 없음",
  ).trim() || "행 정보 없음";

/**
 * 엑셀 대량신청 단계형 모달
 * 1 서식·신청 → 검증 버튼 → 2 검증 → 3 결과·신청 → 성공 N건 신청 → 완료
 */
const BulkExcelModal = ({
  isOpen,
  onClose,
  webMemberId,
  initialTab = "upload",
  endSubmitTime = "17:00",
  isOff = false,
  onDownloadTemplate,
  onValidateFile,
  onSubmitRows,
  onGoMyAds,
}) => {
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const deadlineReloadRef = useRef(null);

  const [tab, setTab] = useState(initialTab);
  const [phase, setPhase] = useState("ready");
  const [progress, setProgress] = useState(0);
  const [toast, setToast] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [rejectedRows, setRejectedRows] = useState([]);
  const [payloadRows, setPayloadRows] = useState([]);
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [registeredCount, setRegisteredCount] = useState(0);
  const [historyJobs, setHistoryJobs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [downloadingFailed, setDownloadingFailed] = useState(false);

  const counts = useMemo(() => {
    const fail = rejectedRows.length;
    const success = payloadRows.length;
    return { total: fail + success, success, fail };
  }, [rejectedRows, payloadRows]);

  const stepIndex = useMemo(() => {
    if (phase === "ready") return 0;
    if (phase === "validating") return 1;
    return 2;
  }, [phase]);

  const resetUploadState = () => {
    setPhase("ready");
    setProgress(0);
    setToast(null);
    setSelectedFile(null);
    setSelectedFileName("");
    setRejectedRows([]);
    setPayloadRows([]);
    setJobId(null);
    setStatus(null);
    setBusy(false);
    setError("");
    setRegisteredCount(0);
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setTab(initialTab);
    resetUploadState();
  }, [isOpen, initialTab]);

  // 주문 마감(end_submit) 시각에 팝업이 열려 있으면 페이지 리프레시
  useEffect(() => {
    if (!isOpen) {
      if (deadlineReloadRef.current) {
        clearTimeout(deadlineReloadRef.current);
        deadlineReloadRef.current = null;
      }
      return undefined;
    }

    const ms = getMsUntilNextEndSubmitKst(endSubmitTime);
    if (ms == null) return undefined;

    deadlineReloadRef.current = setTimeout(() => {
      window.location.reload();
    }, ms);

    return () => {
      if (deadlineReloadRef.current) {
        clearTimeout(deadlineReloadRef.current);
        deadlineReloadRef.current = null;
      }
    };
  }, [isOpen, endSubmitTime]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!isOpen || tab !== "history" || !webMemberId) return undefined;
    let cancelled = false;
    const load = async () => {
      setHistoryLoading(true);
      try {
        const response = await getBulkUploadJobsAPI(webMemberId);
        if (!cancelled) {
          setHistoryJobs(response.data || []);
          setHistoryError("");
        }
      } catch (err) {
        if (!cancelled) {
          setHistoryError(
            err.response?.data?.msg || "내역을 불러오지 못했습니다.",
          );
        }
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, tab, webMemberId]);

  useEffect(() => {
    if (!isOpen || phase !== "registering" || !jobId) return undefined;
    let cancelled = false;

    const poll = async () => {
      try {
        const response = await getBulkUploadStatusAPI(jobId);
        if (cancelled) return;
        const next = response.data;
        setStatus(next);
        setError("");
        const total = next?.totalCount || 0;
        const done =
          (next?.successCount || 0) +
          (next?.failCount || 0) +
          (next?.rejectedCount || 0);
        if (total > 0) {
          setProgress(Math.min(100, Math.round((done / total) * 100)));
        }
        if (next?.status === "DONE") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRegisteredCount(next?.successCount || 0);
          setPhase("complete");
          setToast({
            type: "success",
            message: `${next?.successCount || 0}건 신청이 완료되었습니다.`,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.msg || "진행상황을 불러오지 못했습니다.",
          );
        }
      }
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [isOpen, phase, jobId]);

  if (!isOpen) return null;

  const pickFile = (file) => {
    if (!file) return;
    setSelectedFile(file);
    setSelectedFileName(file.name);
    setError("");
  };

  const handleDrop = (event) => {
    event.preventDefault();
    pickFile(event.dataTransfer?.files?.[0]);
  };

  const handleFileChange = (event) => {
    pickFile(event.target.files?.[0]);
    event.target.value = "";
  };

  const startValidate = async () => {
    if (!selectedFile || busy) return;
    setBusy(true);
    setError("");
    setPhase("validating");
    setProgress(20);
    try {
      const result = await onValidateFile(selectedFile);
      setProgress(100);
      setRejectedRows(result.rejectedRows || []);
      setPayloadRows(result.payloadRows || []);
      const success = (result.payloadRows || []).length;
      const fail = (result.rejectedRows || []).length;
      if (success === 0) {
        setPhase("partial");
        setToast({
          type: "error",
          message: `신청 가능한 행이 없습니다. ${fail}건 모두 검증에 실패했습니다.`,
        });
      } else if (fail > 0) {
        setPhase("partial");
      } else {
        setPhase("allPass");
      }
    } catch (err) {
      setPhase("ready");
      setError(err.message || "검증에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const registerSuccessOnly = async () => {
    if (!payloadRows.length || busy) return;
    setBusy(true);
    setError("");
    setRegisteredCount(payloadRows.length);
    setPhase("registering");
    setProgress(0);
    try {
      const result = await onSubmitRows(payloadRows, rejectedRows);
      const serverRejected = (result.serverRejected || []).map((r) => ({
        rawRow: payloadRows[r.rowIndex]?.rawRow,
        reason: r.reason,
        rowIndex: r.rowIndex,
      }));
      if (serverRejected.length) {
        setRejectedRows((prev) => [...prev, ...serverRejected]);
        const rejectedIndexes = new Set(
          serverRejected.map((r) => r.rowIndex).filter((i) => i != null),
        );
        setPayloadRows((prev) =>
          prev.filter((_, index) => !rejectedIndexes.has(index)),
        );
      }
      setJobId(result.jobId);
    } catch (err) {
      setPhase(counts.fail > 0 ? "partial" : "allPass");
      setError(
        err.response?.data?.msg || err.message || "신청에 실패했습니다.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadFailed = async () => {
    setDownloadingFailed(true);
    try {
      let failedItems = [];
      if (jobId) {
        const response = await getBulkUploadFailedItemsAPI(jobId);
        failedItems = response.data || [];
      }
      const rows = [
        ...rejectedRows.map((r) => ({
          ...(r.rawRow || {}),
          실패사유: r.reason,
        })),
        ...failedItems.map((item) => ({
          ...(item.rawRow || {}),
          실패사유: item.failReason,
        })),
      ];
      if (rows.length === 0) {
        alert("실패한 행이 없습니다.");
        return;
      }
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "실패 목록");
      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(
        workbook,
        `올려줌_엑셀 대량 등록(1.0)_실패 목록_${today}.xlsx`,
      );
    } catch (err) {
      alert(
        `실패 목록을 가져오지 못했습니다: ${
          err.response?.data?.msg || err.message
        }`,
      );
    } finally {
      setDownloadingFailed(false);
    }
  };

  const showSteps = phase !== "complete";
  const failList = rejectedRows;

  return (
    <div className="bulkexcel-overlay" role="dialog" aria-modal="true">
      <div className="bulkexcel-modal" onClick={(e) => e.stopPropagation()}>
        <header className="bulkexcel-header">
          <div>
            <p className="bulkexcel-kicker">네이버 · 쇼핑 방문</p>
            <h1>엑셀 대량 등록</h1>
            {tab === "upload" && phase !== "complete" && (
              <div className="bulkexcel-deadline">
                <DeadlineCountdown endSubmitTime={endSubmitTime} isOff={isOff} />
              </div>
            )}
          </div>
          <button type="button" className="bulkexcel-close" onClick={onClose}>
            닫기
          </button>
        </header>

        {phase !== "complete" && (
          <div className="bulkexcel-tabs">
            <button
              type="button"
              className={tab === "upload" ? "is-active" : ""}
              onClick={() => setTab("upload")}
            >
              신청하기
            </button>
            <button
              type="button"
              className={tab === "history" ? "is-active" : ""}
              onClick={() => setTab("history")}
            >
              내역 확인
            </button>
          </div>
        )}

        {tab === "upload" && (
          <>
            {showSteps && (
              <ol className="bulkexcel-steps">
                {STEPS.map((step, index) => (
                  <li
                    key={step.id}
                    className={
                      index < stepIndex
                        ? "is-done"
                        : index === stepIndex
                          ? "is-current"
                          : ""
                    }
                  >
                    <span>{index + 1}</span>
                    {step.label}
                  </li>
                ))}
              </ol>
            )}

            {error && <p className="bulkexcel-inline-error">{error}</p>}

            {phase === "ready" && (
              <section className="bulkexcel-panel">
                <div className="bulkexcel-hero-card">
                  <div>
                    <h2>서식 다운로드 후 신청하세요</h2>
                    <p>
                      양식에 맞춰 작성한 엑셀을 올린 뒤
                      <br />
                      검증 버튼을 누르면 검증 단계로 이동합니다.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="bulkexcel-btn is-ghost"
                    onClick={onDownloadTemplate}
                  >
                    서식 다운로드
                  </button>
                </div>

                <label
                  className={`bulkexcel-dropzone${
                    selectedFileName ? " is-filled" : ""
                  }`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    hidden
                    onChange={handleFileChange}
                  />
                  {selectedFileName ? (
                    <>
                      <strong>{selectedFileName}</strong>
                      <span>
                        파일이 준비되었습니다. 아래에서 검증을 시작하세요.
                      </span>
                      <em>다른 파일 선택</em>
                    </>
                  ) : (
                    <>
                      <strong>엑셀 파일 선택 또는 끌어다 놓기</strong>
                      <span>.xlsx / .xls · 쇼핑 방문 양식만 지원</span>
                      <em>파일 선택</em>
                    </>
                  )}
                </label>

                <div className="bulkexcel-actions is-single">
                  <button
                    type="button"
                    className="bulkexcel-btn"
                    onClick={startValidate}
                    disabled={!selectedFileName || busy}
                  >
                    검증
                  </button>
                </div>

                <div className="bulkexcel-hint">
                  <p>확인 포인트</p>
                  <ul>
                    <li>
                      파일을 올린 뒤 검증 버튼을 눌러 2번 검증으로 이동합니다.
                    </li>
                    <li>검증이 끝나면 3번 결과·신청으로 자동 이동합니다.</li>
                    <li>
                      실패 행은 목록으로 확인하고 수정 후 재신청할 수 있습니다.
                    </li>
                  </ul>
                </div>
              </section>
            )}

            {phase === "validating" && (
              <section className="bulkexcel-panel bulkexcel-progress">
                <div className="bulkexcel-progress-ring">
                  <strong>{progress}%</strong>
                </div>
                <h2>파일 검증 중</h2>
                <p>필수값·날짜·단가를 확인하고 있습니다.</p>
                <div className="bulkexcel-progress-bar">
                  <span style={{ width: `${progress}%` }} />
                </div>
              </section>
            )}

            {phase === "registering" && (
              <section className="bulkexcel-panel bulkexcel-progress">
                <div className="bulkexcel-progress-ring">
                  <strong>{progress}%</strong>
                </div>
                <h2>
                  {status
                    ? `신청 진행 중 · 성공 ${status.successCount || 0}건`
                    : `성공 ${registeredCount}건 신청 중`}
                </h2>
                <p>
                  광고 신청은 이미 완료됐고, 상품정보는 뒤에서 채워지는
                  중입니다.
                </p>
                <div className="bulkexcel-progress-bar">
                  <span style={{ width: `${Math.max(progress, 8)}%` }} />
                </div>
                {status && (
                  <ul className="bulkexcel-count-row">
                    <li>
                      <b>{status.totalCount || 0}</b>
                      <span>전체</span>
                    </li>
                    <li>
                      <b>{status.successCount || 0}</b>
                      <span>완료</span>
                    </li>
                    <li>
                      <b>
                        {(status.failCount || 0) + (status.rejectedCount || 0)}
                      </b>
                      <span>실패</span>
                    </li>
                  </ul>
                )}
              </section>
            )}

            {(phase === "partial" || phase === "allPass") && (
              <section className="bulkexcel-panel">
                <div
                  className={`bulkexcel-result ${
                    phase === "partial" ? "is-warn" : "is-success"
                  }`}
                >
                  <p className="bulkexcel-result-badge">
                    {phase === "partial" ? "일부 실패" : "검증 완료"}
                  </p>
                  <h2>
                    {phase === "partial" ? (
                      <>
                        {counts.total}건 중 <em>{counts.fail}건</em> 실패
                      </>
                    ) : (
                      <>{counts.total}건 모두 검증 통과</>
                    )}
                  </h2>
                  <p>
                    성공 {counts.success}건 · 실패 {counts.fail}건.
                    {phase === "partial"
                      ? " 실패 목록을 확인한 뒤 수정·재신청하거나 성공 건만 신청하세요."
                      : " 성공 건을 신청하면 신청이 완료됩니다."}
                  </p>
                </div>

                <div className="bulkexcel-count-cards">
                  <div>
                    <b>{counts.total}</b>
                    <span>전체</span>
                  </div>
                  <div className="is-ok">
                    <b>{counts.success}</b>
                    <span>성공</span>
                  </div>
                  <div className={counts.fail ? "is-fail" : ""}>
                    <b>{counts.fail}</b>
                    <span>실패</span>
                  </div>
                </div>

                {phase === "partial" && failList.length > 0 && (
                  <div className="bulkexcel-fail-list">
                    <div className="bulkexcel-fail-head">
                      <h3>실패 목록</h3>
                      <button
                        type="button"
                        className="bulkexcel-btn is-ghost is-small"
                        onClick={handleDownloadFailed}
                        disabled={downloadingFailed}
                      >
                        {downloadingFailed
                          ? "다운로드 중..."
                          : "실패 엑셀 다운로드"}
                      </button>
                    </div>
                    <ul>
                      {failList.slice(0, 50).map((row, index) => (
                        <li key={`${row.rowIndex ?? index}-${index}`}>
                          <span className="bulkexcel-fail-row">
                            {row.rowIndex != null
                              ? `${row.rowIndex}행`
                              : `${index + 1}행`}
                          </span>
                          <div className="bulkexcel-fail-body">
                            <strong>{failRowLabel(row.rawRow)}</strong>
                            <em>{row.reason}</em>
                          </div>
                        </li>
                      ))}
                    </ul>
                    {failList.length > 50 && (
                      <p className="bulkexcel-fail-note">
                        외 {failList.length - 50}건은 실패 엑셀에서 확인하세요.
                      </p>
                    )}
                  </div>
                )}

                <div
                  className={`bulkexcel-actions ${
                    phase === "allPass" ? "is-single" : ""
                  }`}
                >
                  {phase === "partial" && (
                    <button
                      type="button"
                      className="bulkexcel-btn is-ghost"
                      onClick={resetUploadState}
                    >
                      수정 및 재신청
                    </button>
                  )}
                  <button
                    type="button"
                    className="bulkexcel-btn"
                    onClick={registerSuccessOnly}
                    disabled={counts.success === 0 || busy}
                  >
                    성공 {counts.success}건 신청
                  </button>
                </div>
              </section>
            )}

            {phase === "complete" && (
              <section className="bulkexcel-panel bulkexcel-complete">
                <div className="bulkexcel-complete-check" aria-hidden="true">
                  <svg viewBox="0 0 64 64" className="bulkexcel-complete-svg">
                    <circle
                      className="bulkexcel-complete-ring"
                      cx="32"
                      cy="32"
                      r="28"
                    />
                    <path
                      className="bulkexcel-complete-mark"
                      d="M20 33.5 L28.5 42 L45 24"
                      fill="none"
                    />
                  </svg>
                </div>
                <h2>광고 신청이 완료되었습니다.</h2>
                <p>
                  {status?.successCount ?? registeredCount}건이 신청되었습니다.
                  <br />
                  신청하신 광고는 관리자 검토 후 승인됩니다.
                </p>
                <div
                  className="bulkexcel-complete-meta"
                  aria-label="신청 요약"
                >
                  <div>
                    <span>광고 유형</span>
                    <strong>쇼핑 방문</strong>
                  </div>
                  <div className="bulkexcel-complete-meta-divider" />
                  <div>
                    <span>신청 건수</span>
                    <strong>
                      {status?.successCount ?? registeredCount}건
                    </strong>
                  </div>
                </div>
                {(status?.failCount > 0 ||
                  status?.rejectedCount > 0 ||
                  rejectedRows.length > 0) && (
                  <button
                    type="button"
                    className="bulkexcel-btn is-ghost"
                    onClick={handleDownloadFailed}
                    disabled={downloadingFailed}
                    style={{ width: "100%", marginBottom: 8 }}
                  >
                    실패 목록 다운로드
                  </button>
                )}
                <div className="bulkexcel-actions">
                  <button
                    type="button"
                    className="bulkexcel-btn is-ghost"
                    onClick={resetUploadState}
                  >
                    처음으로
                  </button>
                  <button
                    type="button"
                    className="bulkexcel-btn"
                    onClick={() => {
                      onClose?.();
                      onGoMyAds?.();
                    }}
                  >
                    신청한 광고 확인
                  </button>
                </div>
              </section>
            )}
          </>
        )}

        {tab === "history" && phase !== "complete" && (
          <section className="bulkexcel-panel">
            <div className="bulkexcel-history-head">
              <h2>대량신청 내역</h2>
              <p>최근 업로드 작업의 성공·실패 결과를 확인할 수 있습니다.</p>
            </div>
            {historyLoading && (
              <p className="bulkexcel-history-message">불러오는 중...</p>
            )}
            {!historyLoading && historyError && (
              <p className="bulkexcel-history-message is-error">
                {historyError}
              </p>
            )}
            {!historyLoading && !historyError && historyJobs.length === 0 && (
              <p className="bulkexcel-history-message">
                아직 엑셀로 신청한 내역이 없습니다.
              </p>
            )}
            {!historyLoading && !historyError && historyJobs.length > 0 && (
              <div className="bulkexcel-history-list">
                {historyJobs.map((job) => {
                  const fail =
                    (job.failCount || 0) + (job.rejectedCount || 0);
                  return (
                    <article key={job.job_id}>
                      <div>
                        <strong>{formatJobDate(job.createdAt)}</strong>
                        <span
                          className={`bulkexcel-status is-${
                            fail ? "warn" : "ok"
                          }`}
                        >
                          {job.status === "DONE" ? "완료" : "처리 중"}
                        </span>
                      </div>
                      <p>
                        전체 {job.totalCount} · 성공 {job.successCount} · 실패{" "}
                        {fail}
                      </p>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {toast && (
          <div className={`bulkexcel-toast is-${toast.type}`} role="status">
            {toast.message}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkExcelModal;
